import { useEffect, useRef } from 'react'
import { authService } from '@/services/authService'
import {
  ACTIVITY_EVENTS,
  CHECK_INTERVAL_MS,
  IDLE_LIMIT_MS,
  LAST_ACTIVITY_KEY,
  REFRESH_MARGIN_MS,
  readLastActivity,
  readTokenExpiry,
  writeLastActivity,
  type SessionEndReason,
} from '@/lib/sessionTimeout'

/**
 * Ends the session after an hour of inactivity, or when the access token
 * expires, whichever comes first.
 *
 * While the person is active the token is renewed before it expires, so working
 * through the hour mark does not throw them out mid-sentence. Stop touching the
 * console and the renewals stop with you.
 *
 * Signing out goes through /logout, the one route that actually ends a Synstream
 * session: only the console holds the refresh token, and the dashboard and the
 * pipeline editor are signed in against the same one.
 */
export function useSessionTimeout(isAuthenticated: boolean): void {
  // Guards the redirect. Without it a slow unload lets the next tick fire and
  // navigate again, and a refresh that resolves afterwards revives the session.
  const endingRef = useRef(false)
  const refreshingRef = useRef(false)

  useEffect(() => {
    if (!isAuthenticated) {
      endingRef.current = false
      return
    }

    // /logout has a stored user until its own call returns, and /login may too
    // if someone navigates back to it. Watching either would send an expired
    // session to /logout from /logout, which is a loop.
    const path = window.location.pathname
    if (path === '/logout' || path === '/login') return

    // Signing in counts as activity; without this the clock would carry over
    // from whatever the previous session left behind.
    writeLastActivity()

    const endSession = (reason: SessionEndReason) => {
      if (endingRef.current) return
      endingRef.current = true
      const here = window.location.pathname + window.location.search
      const params = new URLSearchParams({ reason })
      if (here && here !== '/') params.set('redirect', here)
      window.location.assign(`/logout?${params.toString()}`)
    }

    const noteActivity = () => {
      if (endingRef.current) return
      writeLastActivity()
    }

    // Throttled to once a second: these fire on every pointer move in a drag,
    // and a localStorage write per event is a real cost on a slow machine.
    let lastWrite = 0
    const onActivity = () => {
      const now = Date.now()
      if (now - lastWrite < 1000) return
      lastWrite = now
      noteActivity()
    }

    // Another tab's activity keeps this one alive.
    const onStorage = (event: StorageEvent) => {
      if (event.key === LAST_ACTIVITY_KEY) lastWrite = Date.now()
    }

    // Coming back to the tab is activity in itself, and it is also the moment a
    // laptop that was asleep gets its first honest look at the clock.
    const onVisibility = () => {
      if (document.visibilityState === 'visible') noteActivity()
    }

    const check = async () => {
      if (endingRef.current) return

      const now = Date.now()
      const idleFor = now - readLastActivity()

      if (idleFor >= IDLE_LIMIT_MS) {
        endSession('idle')
        return
      }

      const expiresAt = readTokenExpiry(authService.getAccessToken())

      // No readable expiry means no usable token. Nothing behind this page will
      // authenticate, so say so instead of leaving a console that looks alive.
      if (expiresAt === null || now >= expiresAt) {
        endSession('expired')
        return
      }

      if (expiresAt - now <= REFRESH_MARGIN_MS && !refreshingRef.current) {
        refreshingRef.current = true
        try {
          await authService.refreshToken()
        } catch {
          // refreshToken clears the stored session on failure, so there is
          // nothing left to work with.
          endSession('expired')
        } finally {
          refreshingRef.current = false
        }
      }
    }

    ACTIVITY_EVENTS.forEach(event =>
      window.addEventListener(event, onActivity, { passive: true })
    )
    window.addEventListener('storage', onStorage)
    document.addEventListener('visibilitychange', onVisibility)

    // Check once on mount as well as on the interval: a tab restored after the
    // laptop slept should not wait another interval to notice.
    void check()
    const timer = window.setInterval(() => void check(), CHECK_INTERVAL_MS)

    return () => {
      ACTIVITY_EVENTS.forEach(event => window.removeEventListener(event, onActivity))
      window.removeEventListener('storage', onStorage)
      document.removeEventListener('visibilitychange', onVisibility)
      window.clearInterval(timer)
    }
  }, [isAuthenticated])
}
